/**
 * Le forfait d'une tournée (lot U4, ADR-0016) : la logique SANS écran. Un montant HT facturé à chaque occurrence, en plus de la
 * main d'œuvre et du déplacement (les trois se cumulent). Donnée financière : ce module n'est utilisé que pour qui voit les prix.
 *
 * Le montant se saisit à la française (« 450,5 » → « 450.50 »), jamais un `number` : une chaîne décimale de bout en bout.
 */
import type { ForfaitTourneeVue } from '../api/client';
import { lireDecimal } from './bon-edition';

export const PLAFOND_LIBELLE_FORFAIT = 120;
/** Au-delà, c'est une faute de frappe : le serveur refuse aussi. */
export const PLAFOND_FORFAIT_EUROS = 1_000_000;

export interface SaisieForfait {
  readonly libelle: string;
  readonly montantTexte: string;
}

export function saisieDepuisForfait(f: ForfaitTourneeVue['forfait']): SaisieForfait {
  return f === null ? { libelle: '', montantTexte: '' } : { libelle: f.libelle ?? '', montantTexte: montantPourSaisie(f.montant) };
}

/** « 450.50 » → « 450,50 » : ce que l'on retape sans réfléchir. */
export function montantPourSaisie(montant: string): string {
  return montant.replace('.', ',');
}

/** « 450.50 » → « 450,50 € HT », avec l'espace insécable des milliers (« 1 200,00 € HT »). */
export function montantAfficheForfait(montant: string): string {
  const [entier = '0', decimales = '00'] = montant.split('.');
  const groupes = entier.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return `${groupes},${decimales.padEnd(2, '0')}\u00A0€ HT`;
}

export type LectureForfait =
  | { readonly ok: true; readonly retrait: false; readonly libelle: string | null; readonly montant: string }
  | { readonly ok: true; readonly retrait: true }
  | { readonly ok: false; readonly erreur: string };

/**
 * Ce que l'écran envoie. Un montant vide RETIRE le forfait (le libellé seul n'existe pas) ; un montant nul, négatif, à plus de deux
 * décimales ou de plus d'un million est refusé AVANT l'envoi, dans les mêmes termes que le serveur.
 */
export function lireForfait(s: SaisieForfait): LectureForfait {
  const libelle = s.libelle.trim();
  if (libelle.length > PLAFOND_LIBELLE_FORFAIT) return { ok: false, erreur: `Le libellé du forfait : ${PLAFOND_LIBELLE_FORFAIT} caractères au plus.` };
  const lu = lireDecimal(s.montantTexte, { negatifPermis: false, chiffresEntiers: 7, decimales: 2 });
  if (!lu.ok) return { ok: false, erreur: lu.erreur };
  if (lu.vide) {
    if (libelle !== '') return { ok: false, erreur: 'Un libellé sans montant ne fait pas un forfait : saisissez le montant, ou effacez le libellé pour retirer le forfait.' };
    return { ok: true, retrait: true };
  }
  if (Number(lu.valeur) === 0) return { ok: false, erreur: 'Un forfait de 0 € n’a pas de sens : videz le montant pour le retirer.' };
  if (Number(lu.valeur) > PLAFOND_FORFAIT_EUROS) return { ok: false, erreur: 'Un forfait dépasse 1 000 000 € HT par occurrence : vérifiez le montant.' };
  return { ok: true, retrait: false, libelle: libelle === '' ? null : libelle, montant: lu.valeur };
}

/** Le corps du `PUT` : le jeton de la tournée et le forfait voulu, ou `montant: null` pour le retirer. */
export function corpsForfait(revision: number, l: Extract<LectureForfait, { ok: true }>): { revision: number; libelle?: string | null; montant: string | null } {
  return l.retrait ? { revision, montant: null } : { revision, libelle: l.libelle, montant: l.montant };
}

/** Rien à enregistrer quand la saisie dit la même chose que le forfait posé. */
export function forfaitInchange(f: ForfaitTourneeVue['forfait'], l: Extract<LectureForfait, { ok: true }>): boolean {
  if (l.retrait) return f === null;
  return f !== null && Number(f.montant) === Number(l.montant) && (f.libelle ?? null) === l.libelle;
}

export function phraseForfait(f: ForfaitTourneeVue['forfait']): string {
  if (f === null) return 'Aucun forfait : seuls la main d’œuvre et le déplacement sont facturés.';
  const nom = f.libelle ? `« ${f.libelle} » : ` : '';
  return `${nom}${montantAfficheForfait(f.montant)} à chaque occurrence, en plus de la main d’œuvre et du déplacement.`;
}

export function phraseResultatForfait(f: ForfaitTourneeVue['forfait']): string {
  return f === null ? 'Forfait retiré.' : `Forfait enregistré : ${montantAfficheForfait(f.montant)} à chaque occurrence.`;
}
